import { hasDuplicateName, normalizeName } from "@/lib/duplicate-name";

/**
 * Duplicate-name decisions for the Documents page, kept out of the handlers so they can be tested.
 *
 * genesis enforces NO uniqueness on document_name, curriculum_vitae.name or document_group.name, so
 * these are the only enforcement — see the note in lib/duplicate-name.ts. Every rule here is
 * case-insensitive and trim-insensitive, because two rows that read the same in the list ARE the same
 * to the person reading it.
 */

/** A stored item that occupies a name. */
export interface NamedItem {
  id: string;
  name: string;
}

/**
 * Splits an upload batch into files that can be stored and files whose name is already taken.
 *
 * Uploading over an existing name leaves two indistinguishable rows, and any later name-based lookup
 * (ExtractionDialog reuses a stored document by name) then picks one of them arbitrarily. Collisions
 * WITHIN the batch count too: two files named the same would otherwise both be accepted.
 */
export function partitionUploads<T extends { name: string }>(
  files: readonly T[],
  stored: readonly NamedItem[],
): { uploadable: T[]; taken: T[] } {
  const names = stored.map((d) => d.name);
  const uploadable: T[] = [];
  const taken: T[] = [];
  const claimed = new Set<string>();
  for (const file of files) {
    const key = normalizeName(file.name);
    if (hasDuplicateName(names, file.name) || claimed.has(key)) {
      taken.push(file);
      continue;
    }
    claimed.add(key);
    uploadable.push(file);
  }
  return { uploadable, taken };
}

/**
 * The first staged rename that would collide, or null when the whole batch is safe.
 *
 * Checked as a batch rather than per row because renames are staged and flushed together: two rows
 * renamed to the SAME new name collide with each other even though neither collides with a stored
 * name. Returning the offender rather than a boolean lets the caller point at it.
 */
export function firstRenameCollision(
  staged: ReadonlyArray<readonly [id: string, name: string]>,
  stored: readonly NamedItem[],
): { id: string; name: string } | null {
  const seen = new Set<string>();
  for (const [id, raw] of staged) {
    const name = raw.trim();
    if (!name) continue; // the blank-name rule is a separate, earlier guard
    // Exclude the row being renamed so keeping its own name is never a collision.
    const others = stored.filter((d) => d.id !== id).map((d) => d.name);
    const key = normalizeName(name);
    if (hasDuplicateName(others, name) || seen.has(key)) return { id, name };
    seen.add(key);
  }
  return null;
}

/** Whether a folder name is already used, ignoring the folder being renamed. */
export function folderNameTaken(
  folders: readonly NamedItem[],
  name: string,
  exceptId?: string,
): boolean {
  const own = exceptId === undefined ? null : (folders.find((f) => f.id === exceptId)?.name ?? null);
  return hasDuplicateName(folders.map((f) => f.name), name, own);
}
